import { createContext } from 'ui/lib/react-utils'
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { aihubStorage, createUuid } from 'shared-utils'
import { type AiOfferAnalyticsRequestDTO, type CartItem, type ProductLightDTO } from 'ecosystem'
import useGenericRequest from 'api/utils/next/useGenericRequest'
import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import { type ConsentState, useGTMContext } from '../data-collection'
import { useNotificationsContext } from '../notifications'

interface AihubAnalyticsProviderPureState {
  userSessionId: string | null
  visitProductPage: (product: ProductLightDTO) => void
}

const [AihubAnalyticsProviderPure, useAihubAnalyticsContext] =
  createContext<AihubAnalyticsProviderPureState>({
    name: 'AihubAnalytics'
  })

export { useAihubAnalyticsContext }

/**
 * The analytics user id is a UUID persisted in localStorage, so unlike a data
 * layer push it IS the act consent governs (ePrivacy Art. 5(3)). It stays gated.
 *
 * `NEXT_PUBLIC_AIHUB_SKIP_CONSENT=true` is the per-store escape hatch, for a
 * storefront that has no usable consent signal at all — no `GTMProvider`, or a
 * CMP the storefront cannot observe. It is not a convenience toggle: with it on,
 * the identifier is written without consent.
 */
const SKIP_CONSENT_FROM_ENV = process.env.NEXT_PUBLIC_AIHUB_SKIP_CONSENT === 'true'
const IS_AI_DEALS_ENABLED = process.env.NEXT_PUBLIC_AI_DEALS_ENABLED === 'true'

type AihubAnalyticsProviderProps = PropsWithChildren & {
  /** Overrides `NEXT_PUBLIC_AIHUB_SKIP_CONSENT`. Leave unset so the store decides. */
  skipConsent?: boolean
}

export const AihubAnalyticsProvider = ({
  children,
  skipConsent: skipConsentProp
}: AihubAnalyticsProviderProps) => {
  const skipConsent = skipConsentProp ?? SKIP_CONSENT_FROM_ENV
  const { token } = useNotificationsContext()
  const { consentManager, isInitialized } = useGTMContext()
  const [userSessionId, setUserSessionId] = useState<string | null>(null)
  const { fetchNextRoute: fetchAihubAnalytic } = useGenericRequest<void>()

  const applyNewUserSessionId = useCallback(() => {
    const id = aihubStorage.getAnalyticUserId() ?? createUuid()
    setUserSessionId(id)
    aihubStorage.setAnalyticUserId(id)
  }, [])

  useEffect(() => {
    // Nothing below may write an identifier when the feature that consumes it is
    // switched off for this store — `sendEvent` already refuses to send.
    if (!IS_AI_DEALS_ENABLED) {
      return
    }

    if (skipConsent) {
      applyNewUserSessionId()
      return
    }

    if (!isInitialized) {
      return
    }

    const removeUserSessionId = () => {
      setUserSessionId(null)
      aihubStorage.removeAnalyticUserId()
    }

    // Read, don't clear: a CMP that loads late still reports the default `denied`
    // here, and clearing on that would mint a fresh UUID on every visit once the
    // real state arrives. Only an actual revocation clears.
    if (consentManager?.getCurrentConsentState()?.analytics) {
      applyNewUserSessionId()
    }

    const handleConsentUpdate = (state: ConsentState) => {
      if (state?.analytics) {
        applyNewUserSessionId()
      } else {
        removeUserSessionId()
      }
    }

    consentManager?.onConsentUpdate(handleConsentUpdate)

    return () => {
      consentManager?.removeConsentUpdateCallback(handleConsentUpdate)
    }
  }, [applyNewUserSessionId, consentManager, isInitialized, skipConsent])

  const sendEvent = useCallback(
    (params: Omit<AiOfferAnalyticsRequestDTO, 'token' | 'userSessionId'>) => {
      if (!userSessionId || !IS_AI_DEALS_ENABLED) {
        return
      }

      fetchAihubAnalytic('/api/ai-offers/send-analytics', {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          token,
          userSessionId,
          ...params
        } as AiOfferAnalyticsRequestDTO)
      })
    },
    [fetchAihubAnalytic, token, userSessionId]
  )

  const visitProductPage = useCallback(
    (product: ProductLightDTO) => {
      sendEvent({
        event: 'visitProductPage',
        data: {
          product: {
            id: product.id,
            slug: product.slug
          }
        }
      })
    },
    [sendEvent]
  )

  return (
    <AihubAnalyticsProviderPure
      value={{
        userSessionId,
        visitProductPage
      }}
    >
      {children}
    </AihubAnalyticsProviderPure>
  )
}

/**
 * Tracks a user's visit to a product page and determines whether to send analytics data.
 *
 * - If a user stays on the product page for more than 20 seconds, the visit is considered
 * significant.
 * - If the user leaves after this time, analytics data is sent to track engagement.
 * - However, if the product is added to the cart, the timer is cleared, and analytics are not
 * sent,
 *   avoiding unnecessary discount triggers when the user is already interested in purchasing.
 */
export const useAihubAnalyticsVisitPageView = ({
  product,
  cartItems
}: {
  product: ProductLightDTO
  cartItems: CartItem[]
}) => {
  const { visitProductPage } = useAihubAnalyticsContext()
  const timerId = useRef(NaN)
  const needToSendAnalytics = useRef(false)
  const visitProductPageRef = useRefUpdated(visitProductPage)

  useEffect(() => {
    timerId.current = window.setTimeout(() => {
      needToSendAnalytics.current = true
    }, 20000)

    return () => {
      clearTimeout(timerId.current)

      if (needToSendAnalytics.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- function should be updated when it's called
        visitProductPageRef.current(product)
      }
    }
  }, [product, visitProductPageRef])

  useEffect(() => {
    const isProductInCart = cartItems.some((item) => {
      if (item.type === 'SINGLE') {
        return item.meta.singleType.product.id === product.id
      }

      return item.meta.groupType.items.some((p) => p.product.id === product.id)
    })

    if (isProductInCart) {
      clearTimeout(timerId.current)
      needToSendAnalytics.current = false
    }
  }, [cartItems, product.id])
}
